import { useState, useEffect, useCallback } from 'react'
import { BrCard, BrButton } from '@govbr-ds/react-components'
import {
    getAllExpertSessions,
    type ExpertSession,
    type IQAIEvaluation,
    type IQAIRatings,
} from '../services/firestore-proxy'

interface ExpertSummary {
    total_evaluations: number
    interfaces_evaluated: number
    evaluators_count: number
    completed_sessions: number
    in_progress_sessions: number
    avg_iqai_score: number
    avg_clarity: number
    avg_adequacy: number
    avg_consistency: number
    avg_accessibility: number
    avg_usability: number
    avg_time_per_interface: number
}

interface FlatEvaluation extends IQAIEvaluation {
    evaluator_expertise: string
    evaluator_experience: string
    evaluator_education: string
    recovery_code: string
    session_status: string
}

// Calculate IQAI from ratings (0-100 scale)
function calculateIqai(ratings: IQAIRatings): number {
    const sum = ratings.clarity + ratings.adequacy + ratings.consistency + ratings.accessibility + ratings.usability
    return Math.round(((sum - 5) / 20) * 100)
}

// Format expertise label
function formatExpertise(expertise?: string): string {
    const labels: Record<string, string> = {
        'ux_ui_design': 'UX/UI Design',
        'product_management': 'Product Management',
        'frontend_dev': 'Frontend Dev',
        'accessibility': 'Acessibilidade',
        'other': 'Outro',
    }
    return labels[expertise || ''] || expertise || 'N/A'
}

// Format experience label
function formatExperience(exp?: string): string {
    const labels: Record<string, string> = {
        '<1': '<1 ano',
        '1-2': '1-2 anos',
        '2-5': '2-5 anos',
        '5-10': '5-10 anos',
        '>10': '>10 anos',
    }
    return labels[exp || ''] || exp || 'N/A'
}

// Format education label
function formatEducation(edu?: string): string {
    const labels: Record<string, string> = {
        'undergrad_incomplete': 'Graduação (incompleta)',
        'undergrad_complete': 'Graduação',
        'specialization': 'Especialização',
        'masters': 'Mestrado',
        'doctorate': 'Doutorado',
    }
    return labels[edu || ''] || edu || 'N/A'
}

// Check if expert meets qualification criteria per validation doc section 5.2
// Requires: ≥2 years experience AND expertise in one of 4 valid areas (excludes 'other') AND undergrad complete or above
function isQualifiedExpert(profile?: { expertise?: string; years_experience?: string; education?: string }): boolean {
    if (!profile) return false

    // Min 1 year experience
    const validExperience = ['1-2', '2-5', '5-10', '>10']
    if (!validExperience.includes(profile.years_experience || '')) return false

    // Valid expertise areas (excludes 'other')
    const validExpertise = [
        'ux_ui_design',
        'product_management',
        'frontend_dev',
        'backend_dev',
        'qa_quality',
        'business_analysis',
        'information_architecture',
        'accessibility'
    ]
    if (!validExpertise.includes(profile.expertise || '')) return false

    // Min undergrad complete (excludes undergrad_incomplete)
    const validEducation = ['undergrad_complete', 'specialization', 'masters', 'doctorate']
    if (!validEducation.includes(profile.education || '')) return false

    return true
}

// Calculate summary from sessions
function calculateExpertSummary(
    sessions: ExpertSession[],
    completedOnly: boolean = false,
    qualifiedOnly: boolean = false
): ExpertSummary | null {
    // Apply filters
    let filteredSessions = sessions

    if (completedOnly) {
        filteredSessions = filteredSessions.filter(s => s.status === 'completed')
    }

    if (qualifiedOnly) {
        filteredSessions = filteredSessions.filter(s => isQualifiedExpert(s.profile))
    }

    const allEvaluations: IQAIEvaluation[] = []
    const evaluatorIds = new Set<string>()

    let completedCount = 0
    let inProgressCount = 0
    let totalTimeSeconds = 0

    for (const session of filteredSessions) {
        evaluatorIds.add(session.expert_id)

        if (session.status === 'completed') completedCount++
        else if (session.status === 'in_progress') inProgressCount++

        if (session.evaluations && session.evaluations.length > 0) {
            allEvaluations.push(...session.evaluations)
            for (const eval_ of session.evaluations) {
                totalTimeSeconds += eval_.time_spent_seconds || 0
            }
        }
    }

    if (allEvaluations.length === 0) {
        return null
    }

    const interfaceIds = new Set(allEvaluations.map(e => e.execution_id))

    const totals = allEvaluations.reduce(
        (acc, e) => ({
            clarity: acc.clarity + e.ratings.clarity,
            adequacy: acc.adequacy + e.ratings.adequacy,
            consistency: acc.consistency + e.ratings.consistency,
            accessibility: acc.accessibility + e.ratings.accessibility,
            usability: acc.usability + e.ratings.usability,
        }),
        { clarity: 0, adequacy: 0, consistency: 0, accessibility: 0, usability: 0 }
    )

    const n = allEvaluations.length
    const totalIqai = allEvaluations.reduce((acc, e) => acc + (e.iqai_score || calculateIqai(e.ratings)), 0)

    return {
        total_evaluations: n,
        interfaces_evaluated: interfaceIds.size,
        evaluators_count: evaluatorIds.size,
        completed_sessions: completedCount,
        in_progress_sessions: inProgressCount,
        avg_iqai_score: totalIqai / n,
        avg_clarity: totals.clarity / n,
        avg_adequacy: totals.adequacy / n,
        avg_consistency: totals.consistency / n,
        avg_accessibility: totals.accessibility / n,
        avg_usability: totals.usability / n,
        avg_time_per_interface: totalTimeSeconds / n,
    }
}

// Flatten evaluations with session info
function flattenEvaluations(sessions: ExpertSession[]): FlatEvaluation[] {
    const evaluations: FlatEvaluation[] = []

    for (const session of sessions) {
        if (session.evaluations) {
            for (const evaluation of session.evaluations) {
                evaluations.push({
                    ...evaluation,
                    evaluator_expertise: formatExpertise(session.profile?.expertise),
                    evaluator_experience: formatExperience(session.profile?.years_experience),
                    evaluator_education: formatEducation(session.profile?.education),
                    recovery_code: session.recovery_code,
                    session_status: session.status,
                })
            }
        }
    }

    // Sort by date (most recent first)
    return evaluations.sort((a, b) =>
        new Date(b.evaluated_at).getTime() - new Date(a.evaluated_at).getTime()
    )
}

// Session Detail Modal Component
function SessionDetailModal({
    session,
    onClose,
}: {
    session: ExpertSession
    onClose: () => void
}) {
    const sessionIqai = session.evaluations.length > 0
        ? session.evaluations.reduce((acc, e) => acc + (e.iqai_score || calculateIqai(e.ratings)), 0) / session.evaluations.length
        : 0

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}
            onClick={onClose}
        >
            <div
                className="bg-pure-0 rounded shadow-lg w-100 mx-3"
                style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center p-4 border-b bg-gray-2">
                    <div>
                        <h2 className="text-xl font-bold text-gray-80 mb-1">
                            Sessão {session.recovery_code}
                        </h2>
                        <span className={`badge ${session.status === 'completed' ? 'bg-success' : session.status === 'in_progress' ? 'bg-warning' : 'bg-gray-40'} text-pure-0 px-2 py-1 rounded text-xs`}>
                            {session.status === 'completed' ? 'Concluído' : session.status === 'in_progress' ? 'Em andamento' : 'Abandonado'}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-link text-gray-60 p-2"
                        aria-label="Fechar"
                    >
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 d-flex flex-column gap-4">
                    {/* Expert Profile */}
                    <div className="bg-gray-2 p-3 rounded">
                        <h3 className="font-semibold text-gray-80 mb-3">
                            <i className="fas fa-user me-2"></i>
                            Perfil do Especialista
                        </h3>
                        <div className="d-flex flex-wrap gap-4">
                            <div>
                                <p className="text-xs text-gray-50 mb-1">Expertise</p>
                                <p className="font-medium">{formatExpertise(session.profile?.expertise)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-50 mb-1">Experiência</p>
                                <p className="font-medium">{formatExperience(session.profile?.years_experience)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-50 mb-1">Formação</p>
                                <p className="font-medium">{formatEducation(session.profile?.education)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-50 mb-1">Qualificado</p>
                                <p className="font-medium">
                                    {isQualifiedExpert(session.profile) ? (
                                        <span className="text-success"><i className="fas fa-check me-1"></i>Sim</span>
                                    ) : (
                                        <span className="text-gray-50"><i className="fas fa-minus me-1"></i>Não</span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-50 mb-1">Início da Sessão</p>
                                <p className="font-medium">{new Date(session.started_at).toLocaleString('pt-BR')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Session Summary */}
                    <div className="d-flex gap-4">
                        <div className="flex-1 text-center bg-gray-2 p-3 rounded">
                            <p className={`text-2xl font-bold ${sessionIqai >= 75 ? 'text-success' : sessionIqai >= 50 ? 'text-warning' : 'text-danger'}`}>
                                {sessionIqai.toFixed(0)}%
                            </p>
                            <p className="text-xs text-gray-50">IQAI Médio</p>
                        </div>
                        <div className="flex-1 text-center bg-gray-2 p-3 rounded">
                            <p className="text-2xl font-bold text-primary-default">
                                {session.evaluations.length} / {session.interfaces_to_evaluate.length}
                            </p>
                            <p className="text-xs text-gray-50">Interfaces Avaliadas</p>
                        </div>
                        <div className="flex-1 text-center bg-gray-2 p-3 rounded">
                            <p className="text-2xl font-bold text-primary-default">
                                {session.evaluations.filter(e => e.comments && e.comments.trim()).length}
                            </p>
                            <p className="text-xs text-gray-50">Com Comentários</p>
                        </div>
                    </div>

                    {/* Evaluations List */}
                    <div>
                        <h3 className="font-semibold text-gray-80 mb-3">
                            <i className="fas fa-clipboard-check me-2"></i>
                            Avaliações ({session.evaluations.length})
                        </h3>
                        {session.evaluations.length === 0 ? (
                            <p className="text-gray-50 text-center py-4">
                                Nenhuma avaliação realizada ainda.
                            </p>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {session.evaluations.map((evaluation, idx) => {
                                    const iqaiScore = evaluation.iqai_score || calculateIqai(evaluation.ratings)
                                    return (
                                        <div key={`${evaluation.execution_id}-${idx}`} className="border rounded p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <p className="font-medium text-gray-80">
                                                        Interface #{idx + 1}: {evaluation.profile_id}
                                                    </p>
                                                    <p className="text-xs text-gray-50">
                                                        {evaluation.task_type} • {evaluation.process_phase}
                                                    </p>
                                                </div>
                                                <div className="text-end">
                                                    <p className={`text-xl font-bold ${iqaiScore >= 75 ? 'text-success' : iqaiScore >= 50 ? 'text-warning' : 'text-danger'}`}>
                                                        {iqaiScore}%
                                                    </p>
                                                    <p className="text-xs text-gray-50">
                                                        {Math.round(evaluation.time_spent_seconds / 60)}min • {new Date(evaluation.evaluated_at).toLocaleDateString('pt-BR')}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Ratings */}
                                            <div className="d-flex gap-2 flex-wrap mb-2">
                                                <span className="badge bg-gray-10 text-gray-80 px-2 py-1 rounded text-xs">
                                                    CL: {evaluation.ratings.clarity}
                                                </span>
                                                <span className="badge bg-gray-10 text-gray-80 px-2 py-1 rounded text-xs">
                                                    AD: {evaluation.ratings.adequacy}
                                                </span>
                                                <span className="badge bg-gray-10 text-gray-80 px-2 py-1 rounded text-xs">
                                                    CO: {evaluation.ratings.consistency}
                                                </span>
                                                <span className="badge bg-gray-10 text-gray-80 px-2 py-1 rounded text-xs">
                                                    AC: {evaluation.ratings.accessibility}
                                                </span>
                                                <span className="badge bg-gray-10 text-gray-80 px-2 py-1 rounded text-xs">
                                                    US: {evaluation.ratings.usability}
                                                </span>
                                            </div>

                                            {/* Comments */}
                                            {evaluation.comments && evaluation.comments.trim() && (
                                                <div className="bg-blue-warm-vivid-5 p-2 rounded mt-2">
                                                    <p className="text-xs text-gray-50 mb-1">
                                                        <i className="fas fa-comment me-1"></i>
                                                        Comentário:
                                                    </p>
                                                    <p className="text-sm text-gray-80 whitespace-pre-wrap">
                                                        {evaluation.comments}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="d-flex justify-content-end p-4 border-t bg-gray-2">
                    <BrButton secondary onClick={onClose}>
                        Fechar
                    </BrButton>
                </div>
            </div>
        </div>
    )
}

export function IQAIPage() {
    const [sessions, setSessions] = useState<ExpertSession[]>([])
    const [evaluations, setEvaluations] = useState<FlatEvaluation[]>([])
    const [summary, setSummary] = useState<ExpertSummary | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [completedOnly, setCompletedOnly] = useState(false)
    const [qualifiedOnly, setQualifiedOnly] = useState(false)
    const [showSessions, setShowSessions] = useState(false)
    const [selectedSession, setSelectedSession] = useState<ExpertSession | null>(null)

    const loadData = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            const result = await getAllExpertSessions()

            if (result.success && result.data) {
                setSessions(result.data)
                const flatEvaluations = flattenEvaluations(result.data)
                const expertSummary = calculateExpertSummary(result.data, completedOnly)

                setEvaluations(flatEvaluations)
                setSummary(expertSummary)
            } else {
                setError(result.error || 'Erro ao carregar avaliações')
            }
        } catch (err) {
            console.error('Error loading IQAI data:', err)
            setError('Erro ao carregar dados. Verifique a conexão.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadData()
    }, [loadData])

    // Recalculate summary when filter changes
    useEffect(() => {
        if (sessions.length > 0) {
            const expertSummary = calculateExpertSummary(sessions, completedOnly, qualifiedOnly)
            setSummary(expertSummary)
        }
    }, [completedOnly, qualifiedOnly, sessions])

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center min-h-[400px]">
                <div className="d-flex flex-column align-items-center gap-3">
                    <i className="fas fa-spinner fa-spin text-4xl text-primary-default"></i>
                    <p className="text-gray-60">Carregando avaliações do Firestore...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="d-flex flex-column gap-4">
                <BrCard>
                    <div className="card-content text-center py-5">
                        <i className="fas fa-exclamation-triangle text-4xl text-danger mb-3 d-block"></i>
                        <p className="text-danger mb-3">{error}</p>
                        <BrButton primary onClick={loadData}>
                            <i className="fas fa-sync me-2"></i>
                            Tentar Novamente
                        </BrButton>
                    </div>
                </BrCard>
            </div>
        )
    }

    return (
        <div className="d-flex flex-column gap-6">
            <div className="d-flex justify-content-between align-items-center">
                <h1 className="text-2xl font-bold text-gray-80">
                    Avaliações IQAI
                </h1>
                <BrButton secondary onClick={loadData} disabled={loading}>
                    <i className="fas fa-sync me-2"></i>
                    Atualizar
                </BrButton>
            </div>

            {/* IQAI vs TADS Explanation */}
            <BrCard className="bg-blue-warm-vivid-5">
                <div className="card-header">
                    <span className="title">
                        <i className="fas fa-info-circle me-2"></i>
                        IQAI vs TADS: Métricas Complementares
                    </span>
                </div>
                <div className="card-content">
                    <div className="d-flex flex-column gap-3">
                        <div>
                            <h3 className="font-semibold text-gray-80 mb-2">
                                <i className="fas fa-user-check me-2 text-primary-default"></i>
                                IQAI - Interface Quality Assessment Index
                            </h3>
                            <p className="text-sm text-gray-70">
                                <code>IQAI = ((Σ ratings - 5) / 20) × 100</code>
                            </p>
                            <p className="text-sm text-gray-60 mt-1">
                                Avaliação <strong>qualitativa por especialistas</strong> em 5 dimensões: clareza, adequação, consistência, acessibilidade e usabilidade.
                                Captura percepções humanas sobre a qualidade da interface.
                            </p>
                        </div>
                        <div className="border-t pt-3">
                            <h3 className="font-semibold text-gray-80 mb-2">
                                <i className="fas fa-code me-2 text-success-default"></i>
                                TADS - Technical Adaptation and Design System Score
                            </h3>
                            <p className="text-sm text-gray-70">
                                <code>TADS = (Componentes_Válidos / Total) × 100</code>
                            </p>
                            <p className="text-sm text-gray-60 mt-1">
                                Validação <strong>técnica automática</strong> que verifica se os componentes gerados pertencem ao Gov.br Design System oficial (36 componentes + 8 utilitários).
                                Mede aderência técnica ao padrão.
                            </p>
                        </div>
                        <div className="bg-blue-warm-vivid-10 p-3 rounded text-sm">
                            <strong className="text-gray-80">
                                <i className="fas fa-lightbulb me-1"></i>
                                Interpretação:
                            </strong>
                            <p className="text-gray-70 mt-1">
                                • <strong>TADS alto</strong> (≥95%): Interface tecnicamente correta, usa componentes válidos<br/>
                                • <strong>IQAI alto</strong> (≥75%): Interface com boa experiência de usuário segundo especialistas<br/>
                                • <strong>Ideal</strong>: TADS alto + IQAI alto = interface tecnicamente correta E com boa UX
                            </p>
                        </div>
                    </div>
                </div>
            </BrCard>

            {/* Filter Options */}
            <div className="d-flex flex-column gap-2 bg-gray-2 p-3 rounded">
                <div className="d-flex align-items-center gap-2">
                    <input
                        type="checkbox"
                        id="completedOnly"
                        checked={completedOnly}
                        onChange={(e) => setCompletedOnly(e.target.checked)}
                        className="form-check-input"
                    />
                    <label htmlFor="completedOnly" className="text-sm text-gray-70">
                        Calcular IQAI apenas com sessões <strong>finalizadas</strong>
                    </label>
                    {completedOnly && (
                        <span className="text-xs text-primary-default ms-2">
                            ({sessions.filter(s => s.status === 'completed').length} de {sessions.length} sessões)
                        </span>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2">
                    <input
                        type="checkbox"
                        id="qualifiedOnly"
                        checked={qualifiedOnly}
                        onChange={(e) => setQualifiedOnly(e.target.checked)}
                        className="form-check-input"
                    />
                    <label htmlFor="qualifiedOnly" className="text-sm text-gray-70">
                        Calcular IQAI apenas com <strong>especialistas qualificados</strong>
                        <span className="text-xs text-gray-50 ms-1">(≥2 anos exp. em UX/Produtos/Frontend/Acessibilidade + graduação completa ou acima)</span>
                    </label>
                    {qualifiedOnly && (
                        <span className="text-xs text-primary-default ms-2">
                            ({sessions.filter(s => isQualifiedExpert(s.profile)).length} de {sessions.length} sessões)
                        </span>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            {summary ? (
                <div className="d-flex flex-column md:flex-row gap-4">
                    <BrCard className="text-center flex-1">
                        <div className="card-content py-3">
                            <p className="text-3xl font-bold text-primary-default">
                                {summary.avg_iqai_score.toFixed(0)}%
                            </p>
                            <p className="text-sm text-gray-60">IQAI Médio</p>
                            <p className="text-xs text-gray-40 mt-1">
                                {summary.avg_iqai_score >= 75 ? '✓ Acima do threshold' : '⚠ Abaixo do threshold (75%)'}
                            </p>
                        </div>
                    </BrCard>
                    <BrCard className="text-center flex-1">
                        <div className="card-content py-3">
                            <p className="text-3xl font-bold text-primary-default">
                                {summary.total_evaluations}
                            </p>
                            <p className="text-sm text-gray-60">Avaliações</p>
                            <p className="text-xs text-gray-40 mt-1">
                                {summary.interfaces_evaluated} interfaces únicas
                            </p>
                        </div>
                    </BrCard>
                    <BrCard className="text-center flex-1">
                        <div className="card-content py-3">
                            <p className="text-3xl font-bold text-primary-default">
                                {summary.evaluators_count}
                            </p>
                            <p className="text-sm text-gray-60">Avaliadores</p>
                            <p className="text-xs text-gray-40 mt-1">
                                {summary.completed_sessions} concluídos, {summary.in_progress_sessions} em andamento
                            </p>
                        </div>
                    </BrCard>
                    <BrCard className="text-center flex-1">
                        <div className="card-content py-3">
                            <p className="text-3xl font-bold text-primary-default">
                                {(summary.avg_time_per_interface / 60).toFixed(1)}
                            </p>
                            <p className="text-sm text-gray-60">min/interface</p>
                            <p className="text-xs text-gray-40 mt-1">
                                Tempo médio
                            </p>
                        </div>
                    </BrCard>
                </div>
            ) : (
                <BrCard>
                    <div className="card-content text-center py-5">
                        <i className="fas fa-clipboard-list text-4xl text-gray-40 mb-3 d-block"></i>
                        <p className="text-gray-60">
                            Nenhuma avaliação encontrada no Firestore.
                        </p>
                        <p className="text-sm text-gray-40 mt-2">
                            Acesse a área "Especialistas" para realizar avaliações.
                        </p>
                    </div>
                </BrCard>
            )}

            {/* Dimension Averages */}
            {summary && summary.total_evaluations > 0 && (
                <BrCard>
                    <div className="card-header"><span className="title">Médias por Dimensão (Escala 1-5)</span></div>
                    <div className="card-content">
                        <div className="d-flex flex-column md:flex-row gap-4 text-center">
                            <div className="flex-1">
                                <p className="text-2xl font-bold text-gray-80">
                                    {summary.avg_clarity.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-60">Clareza (CL)</p>
                            </div>
                            <div className="flex-1">
                                <p className="text-2xl font-bold text-gray-80">
                                    {summary.avg_adequacy.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-60">Adequação (AD)</p>
                            </div>
                            <div className="flex-1">
                                <p className="text-2xl font-bold text-gray-80">
                                    {summary.avg_consistency.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-60">Consistência (CO)</p>
                            </div>
                            <div className="flex-1">
                                <p className="text-2xl font-bold text-gray-80">
                                    {summary.avg_accessibility.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-60">Acessibilidade (AC)</p>
                            </div>
                            <div className="flex-1">
                                <p className="text-2xl font-bold text-gray-80">
                                    {summary.avg_usability.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-60">Usabilidade (US)</p>
                            </div>
                        </div>
                    </div>
                </BrCard>
            )}

            {/* Sessions Overview */}
            {sessions.length > 0 && (
                <BrCard>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <span className="title">Sessões de Avaliação ({sessions.length})</span>
                        <BrButton
                            secondary
                            size="small"
                            onClick={() => setShowSessions(!showSessions)}
                        >
                            {showSessions ? 'Ocultar' : 'Mostrar'}
                        </BrButton>
                    </div>
                    {showSessions && (
                        <div className="card-content">
                            <div className="overflow-x-auto">
                                <table className="w-100 text-sm">
                                    <thead>
                                        <tr className="border-b text-left">
                                            <th className="py-2 pr-4">Código</th>
                                            <th className="py-2 pr-4">Expertise</th>
                                            <th className="py-2 pr-4">Experiência</th>
                                            <th className="py-2 pr-4 text-center">Avaliações</th>
                                            <th className="py-2 pr-4 text-center">IQAI Médio</th>
                                            <th className="py-2 pr-4">Status</th>
                                            <th className="py-2">Início</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessions.map((session) => {
                                            const sessionIqai = session.evaluations.length > 0
                                                ? session.evaluations.reduce((acc, e) => acc + (e.iqai_score || calculateIqai(e.ratings)), 0) / session.evaluations.length
                                                : 0
                                            const hasComments = session.evaluations.some(e => e.comments && e.comments.trim())
                                            return (
                                                <tr
                                                    key={session.recovery_code}
                                                    className="border-b cursor-pointer hover:bg-gray-2 transition-colors"
                                                    onClick={() => setSelectedSession(session)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Clique para ver detalhes"
                                                >
                                                    <td className="py-2 pr-4 font-mono font-bold">
                                                        <span className="text-primary-default">{session.recovery_code}</span>
                                                        {hasComments && (
                                                            <i className="fas fa-comment text-xs text-gray-40 ms-2" title="Possui comentários"></i>
                                                        )}
                                                    </td>
                                                    <td className="py-2 pr-4">{formatExpertise(session.profile?.expertise)}</td>
                                                    <td className="py-2 pr-4">{formatExperience(session.profile?.years_experience)}</td>
                                                    <td className="py-2 pr-4 text-center">
                                                        {session.evaluations.length} / {session.interfaces_to_evaluate.length}
                                                    </td>
                                                    <td className="py-2 pr-4 text-center">
                                                        <span className={`font-bold ${sessionIqai >= 75 ? 'text-success' : sessionIqai >= 50 ? 'text-warning' : 'text-danger'}`}>
                                                            {sessionIqai.toFixed(0)}%
                                                        </span>
                                                    </td>
                                                    <td className="py-2 pr-4">
                                                        <span className={`badge ${session.status === 'completed' ? 'bg-success' : session.status === 'in_progress' ? 'bg-warning' : 'bg-gray-40'} text-pure-0 px-2 py-1 rounded text-xs`}>
                                                            {session.status === 'completed' ? 'Concluído' : session.status === 'in_progress' ? 'Em andamento' : 'Abandonado'}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 text-gray-60">
                                                        {new Date(session.started_at).toLocaleDateString('pt-BR')}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </BrCard>
            )}

            {/* Evaluations History */}
            <BrCard>
                <div className="card-header"><span className="title">Histórico de Avaliações ({evaluations.length})</span></div>
                <div className="card-content">
                    {evaluations.length === 0 ? (
                        <p className="text-center text-gray-60 py-4">
                            Nenhuma avaliação de especialista realizada ainda.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-100 text-sm">
                                <thead>
                                    <tr className="border-b text-left">
                                        <th className="py-2 pr-4">Perfil</th>
                                        <th className="py-2 pr-4">Expertise</th>
                                        <th className="py-2 pr-4 text-center">CL</th>
                                        <th className="py-2 pr-4 text-center">AD</th>
                                        <th className="py-2 pr-4 text-center">CO</th>
                                        <th className="py-2 pr-4 text-center">AC</th>
                                        <th className="py-2 pr-4 text-center">US</th>
                                        <th className="py-2 pr-4 text-center">IQAI</th>
                                        <th className="py-2 pr-4">Tempo</th>
                                        <th className="py-2 pr-4">Data</th>
                                        <th className="py-2">Comentário</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {evaluations.map((evaluation, idx) => {
                                        const iqaiScore = evaluation.iqai_score || calculateIqai(evaluation.ratings)
                                        const hasComment = evaluation.comments && evaluation.comments.trim()
                                        return (
                                            <tr key={`${evaluation.execution_id}-${idx}`} className="border-b">
                                                <td className="py-2 pr-4 text-xs">
                                                    {evaluation.profile_id}
                                                </td>
                                                <td className="py-2 pr-4 text-xs">
                                                    <span className="text-gray-80">{evaluation.evaluator_expertise}</span>
                                                    <br />
                                                    <span className="text-gray-50">{evaluation.evaluator_experience}</span>
                                                </td>
                                                <td className="py-2 pr-4 text-center">{evaluation.ratings.clarity}</td>
                                                <td className="py-2 pr-4 text-center">{evaluation.ratings.adequacy}</td>
                                                <td className="py-2 pr-4 text-center">{evaluation.ratings.consistency}</td>
                                                <td className="py-2 pr-4 text-center">{evaluation.ratings.accessibility}</td>
                                                <td className="py-2 pr-4 text-center">{evaluation.ratings.usability}</td>
                                                <td className="py-2 pr-4 text-center">
                                                    <span className={`font-bold ${iqaiScore >= 75 ? 'text-success' : iqaiScore >= 50 ? 'text-warning' : 'text-danger'}`}>
                                                        {iqaiScore}%
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-4 text-gray-60">
                                                    {Math.round(evaluation.time_spent_seconds / 60)}min
                                                </td>
                                                <td className="py-2 pr-4 text-gray-60">
                                                    {new Date(evaluation.evaluated_at).toLocaleDateString('pt-BR')}
                                                </td>
                                                <td className="py-2 text-xs" style={{ maxWidth: '200px' }}>
                                                    {hasComment ? (
                                                        <div
                                                            className="text-gray-70 truncate"
                                                            title={evaluation.comments}
                                                            style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        >
                                                            <i className="fas fa-comment text-primary-default me-1"></i>
                                                            {evaluation.comments}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-30">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </BrCard>

            {/* IQAI Explanation */}
            <BrCard>
                <div className="card-header"><span className="title">Sobre o IQAI</span></div>
                <div className="card-content">
                    <div className="prose prose-sm max-w-none text-gray-80">
                        <p>
                            O <strong>IQAI (Índice de Qualidade da Adaptação de Interface)</strong> é uma métrica
                            desenvolvida para avaliar a qualidade das interfaces geradas pelo
                            Framework Lumen.
                        </p>
                        <h4 className="font-semibold mt-4">Dimensões Avaliadas:</h4>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>CL (Clareza):</strong> Clareza e compreensibilidade das informações</li>
                            <li><strong>AD (Adequação):</strong> Adequação ao perfil e contexto do usuário</li>
                            <li><strong>CO (Consistência):</strong> Consistência visual e de navegação</li>
                            <li><strong>AC (Acessibilidade):</strong> Conformidade com padrões de acessibilidade</li>
                            <li><strong>US (Usabilidade):</strong> Facilidade de uso e navegação</li>
                        </ul>
                        <h4 className="font-semibold mt-4">Fórmula:</h4>
                        <p className="font-mono bg-gray-2 p-2 rounded">
                            IQAI = ((CL + AD + CO + AC + US) - 5) / 20 × 100
                        </p>
                        <p className="text-sm text-gray-60 mt-2">
                            Resultado em porcentagem (0% a 100%). Threshold de sucesso: ≥ 75%
                        </p>
                    </div>
                </div>
            </BrCard>

            {/* Session Detail Modal */}
            {selectedSession && (
                <SessionDetailModal
                    session={selectedSession}
                    onClose={() => setSelectedSession(null)}
                />
            )}
        </div>
    )
}
